// react-native-modal-datetime-picker stand-in: a native <input type="date">
// (or datetime-local / time) inside a light modal, styled to the app's
// input field. Same props the app passes: isVisible, mode, date, minimumDate,
// maximumDate, onConfirm(Date), onCancel().

import React, { useEffect, useState } from 'react';
import { Modal, Pressable, StyleSheet, Text, View } from 'react-native-web';

export interface DateTimePickerModalProps {
  isVisible: boolean;
  mode?: 'date' | 'time' | 'datetime';
  date?: Date;
  minimumDate?: Date;
  maximumDate?: Date;
  onConfirm: (date: Date) => void;
  onCancel: () => void;
  confirmTextIOS?: string;
  cancelTextIOS?: string;
}

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

function toInputValue(d: Date, mode: DateTimePickerModalProps['mode']): string {
  const date = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  const time = `${pad(d.getHours())}:${pad(d.getMinutes())}`;
  if (mode === 'time') return time;
  if (mode === 'datetime') return `${date}T${time}`;
  return date;
}

export function fromInputValue(value: string, mode: DateTimePickerModalProps['mode'], base: Date): Date | null {
  if (!value) return null;
  if (mode === 'time') {
    const [h, m] = value.split(':').map(Number);
    const d = new Date(base);
    d.setHours(h, m, 0, 0);
    return d;
  }
  // A bare `YYYY-MM-DD` is parsed as UTC midnight, which is the day before
  // in every timezone west of Greenwich (15 January came back as the 14th).
  // Build the date from its parts so it is local, as the phone's picker is.
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  const d = dateOnly
    ? new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]))
    : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

export default function DateTimePickerModal(props: DateTimePickerModalProps): React.JSX.Element | null {
  const { isVisible, mode = 'date', date, minimumDate, maximumDate, onConfirm, onCancel } = props;
  const initial = date ?? new Date();
  const [value, setValue] = useState(toInputValue(initial, mode));

  useEffect(() => {
    if (isVisible) setValue(toInputValue(date ?? new Date(), mode));
  }, [isVisible, date, mode]);

  if (!isVisible) return null;

  const inputType = mode === 'datetime' ? 'datetime-local' : mode;
  const confirm = () => {
    const parsed = fromInputValue(value, mode, initial);
    if (parsed) onConfirm(parsed);
    else onCancel();
  };

  return (
    <Modal transparent visible animationType="fade" onRequestClose={onCancel}>
      <View style={styles.backdrop}>
        <View style={styles.card} accessibilityRole="dialog">
          <Text style={styles.title}>
            {mode === 'time' ? 'Choose a time' : mode === 'datetime' ? 'Choose a date and time' : 'Choose a date'}
          </Text>
          <input
            type={inputType}
            value={value}
            min={minimumDate ? toInputValue(minimumDate, mode) : undefined}
            max={maximumDate ? toInputValue(maximumDate, mode) : undefined}
            onChange={(e) => setValue(e.target.value)}
            style={inputStyle}
            autoFocus
          />
          <View style={styles.buttons}>
            <Pressable onPress={onCancel} style={[styles.button, styles.buttonCancel]} accessibilityRole="button">
              <Text style={[styles.buttonText, styles.buttonTextCancel]}>{props.cancelTextIOS ?? 'Cancel'}</Text>
            </Pressable>
            <Pressable onPress={confirm} style={styles.button} accessibilityRole="button">
              <Text style={styles.buttonText}>{props.confirmTextIOS ?? 'Confirm'}</Text>
            </Pressable>
          </View>
        </View>
      </View>
    </Modal>
  );
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  boxSizing: 'border-box',
  fontSize: 16,
  color: '#2C4364',
  background: '#FFFFFF',
  border: '1px solid #E5E9F0',
  borderRadius: 50,
  padding: '14px 18px',
  outline: 'none',
  fontFamily: 'inherit',
};

const styles = StyleSheet.create({
  backdrop: {
    flex: 1,
    backgroundColor: 'rgba(28, 44, 79, 0.45)',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
  },
  card: {
    width: '100%',
    maxWidth: 360,
    backgroundColor: '#F3F6F8',
    borderRadius: 16,
    padding: 20,
    gap: 14,
  },
  title: { fontSize: 18, fontWeight: '600', color: '#1C2C4F', textAlign: 'center' },
  buttons: { flexDirection: 'row', gap: 10 },
  button: {
    flex: 1,
    minHeight: 44,
    borderRadius: 25,
    backgroundColor: '#29BFC0',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 11,
  },
  buttonCancel: { backgroundColor: '#FFFFFF', borderWidth: 1, borderColor: '#D8DBDD' },
  buttonText: { color: '#FFFFFF', fontSize: 16, fontWeight: '600' },
  buttonTextCancel: { color: '#1C2C4F' },
});
